import React from 'react';
import { X, AlertCircle, FileText, HelpCircle, Info, Sparkles } from 'lucide-react';
import { APP_VERSION } from '../version';
import { CHANGELOG } from '../changelog';

// The keys the console really responds to (see hooks/useRunSheetKeys).
export const SHORTCUT_GROUPS = [
  {
    title: 'Run-sheet',
    items: [
      ['Stage the next item', '→ or Page Down'],
      ['Stage the previous item', '← or Page Up'],
      ['Send the staged item live', 'Enter'],
      ['Clear the live display', 'B'],
    ],
  },
  {
    title: 'Slides (song lines and long passages)',
    items: [
      ['Next slide', '↓'],
      ['Previous slide', '↑'],
    ],
  },
  {
    title: 'Help',
    items: [['Show these shortcuts', '?']],
  },
];

const TYPE_LABEL = { new: 'New', improved: 'Improved', fixed: 'Fixed' };
const TYPE_STYLE = {
  new: 'bg-emerald-500/15 text-emerald-500',
  improved: 'bg-blue-500/15 text-blue-500',
  fixed: 'bg-amber-500/15 text-amber-500',
};

/** The About / Quick Start Guide / Shortcuts / What's new modals. `kind` is 'about' | 'guide' | 'shortcuts' | 'changelog' (or falsy = closed). */
const HelpModal = ({ kind, onClose, dm, t }) => {
  if (!kind) return null;
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
      <div className={`rounded-xl border shadow-2xl max-w-2xl w-full max-h-[80dvh] overflow-y-auto ${dm ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}>
        {/* About Modal */}
        {kind === 'about' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-2xl font-bold flex items-center gap-2 ${t.textPrimary}`}>
                <Info className="w-6 h-6 text-blue-400" />
                About SleekHear
              </h2>
              <button
                onClick={() => onClose()}
                className={`p-2 rounded-lg transition-all ${t.rowHover}`}
              >
                <X className={`w-5 h-5 ${t.textSecondary}`} />
              </button>
            </div>

            <div className={`space-y-4 ${t.textSecondary}`}>
              <p>
                <strong className={t.textPrimary}>SleekHear</strong> is an AI-powered church presentation system
                that automatically detects Bible verses mentioned during sermons and displays them in real-time.
              </p>

              <div className={`border rounded-lg p-4 ${dm ? 'bg-blue-900/20 border-blue-500/30' : 'bg-blue-50 border-blue-200'}`}>
                <h3 className={`font-bold mb-2 ${t.textPrimary}`}>Key Features</h3>
                <ul className="space-y-1 text-sm">
                  <li>• Service run-sheet, stepped with the arrow keys or a phone</li>
                  <li>• Projector, stage and livestream (OBS) outputs</li>
                  <li>• Voice-activated Bible verse detection</li>
                  <li>• AI-powered song search (speak or sing)</li>
                  <li>• SleekHear AI sermon summarization</li>
                  <li>• AI-enhanced Bible verse detection</li>
                  <li>• Multiple Bible version support</li>
                  <li>• Customizable display settings</li>
                  <li>• Staging and live preview system</li>
                </ul>
              </div>

              <p className={`text-sm ${t.textMuted}`}>
                Version {APP_VERSION}
              </p>
            </div>
          </div>
        )}

        {/* Guide Modal */}
        {kind === 'guide' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-2xl font-bold flex items-center gap-2 ${t.textPrimary}`}>
                <HelpCircle className="w-6 h-6 text-cyan-400" />
                Quick Start Guide
              </h2>
              <button onClick={() => onClose()} className={`p-2 rounded-lg transition-all ${t.rowHover}`}>
                <X className={`w-5 h-5 ${t.textSecondary}`} />
              </button>
            </div>

            <div className={`space-y-6 ${t.textSecondary}`}>
              {[
                { n:1, title:'Open Display Window', body:<>Click the <strong>"Open Display"</strong> button in the top-right corner. Move this window to your projection screen.</> },
                { n:2, title:'Start Voice Recognition', body:<>Click <strong>"Start"</strong> in the Voice section. Allow microphone access when prompted.</> },
                { n:3, title:'Search for Content', body:<>Use <strong>Manual Search</strong> to type a reference — a whole chapter (<strong>Genesis 1</strong>), a single verse (<strong>John 3:16</strong>), or a range of verses (<strong>Genesis 1:5-8</strong>). You can also type part of a book name (like "corin") and pick from the suggestions, or let voice recognition detect verses automatically during the sermon.</> },
                { n:4, title:'Go Live', body:<>Click <strong>"Send Live"</strong> to display content on the projection screen. The <strong>"LIVE"</strong> button will turn red when content is showing.</> },
                { n:5, title:'Customize Settings', body:<>Use the <strong>Settings menu</strong> to adjust font size, font family, text color, and background color. Changes appear instantly in both previews.</> },
              ].map(({ n, title, body }) => (
                <div key={n}>
                  <h3 className={`text-lg font-bold mb-3 flex items-center gap-2 ${t.textPrimary}`}>
                    <span className="bg-cyan-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">{n}</span>
                    {title}
                  </h3>
                  <p className="text-sm ml-8">{body}</p>
                </div>
              ))}

              <div className={`border rounded-lg p-4 ${dm ? 'bg-blue-900/20 border-blue-500/30' : 'bg-blue-50 border-blue-200'}`}>
                <h4 className={`font-bold mb-2 flex items-center gap-2 ${t.textPrimary}`}>
                  <AlertCircle className="w-4 h-4 text-blue-400" />
                  Pro Tips
                </h4>
                <ul className="space-y-1 text-xs">
                  <li>• Search a verse range like <strong>Genesis 1:5-8</strong> to stage several verses at once</li>
                  <li>• Save frequently used verses to your Verse Library</li>
                  <li>• Add custom songs to your Song Library for quick access</li>
                  <li>• Use line-by-line navigation (←/→) for song lyrics</li>
                  <li>• Change Bible version anytime from the sidebar</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* What's new (change log) */}
        {kind === 'changelog' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-2xl font-bold flex items-center gap-2 ${t.textPrimary}`}>
                <Sparkles className="w-6 h-6 text-emerald-400" />
                What's new
              </h2>
              <button onClick={() => onClose()} aria-label="Close" className={`p-2 rounded-lg transition-all ${t.rowHover}`}>
                <X className={`w-5 h-5 ${t.textSecondary}`} />
              </button>
            </div>
            <div className="space-y-8">
              {CHANGELOG.map(release => (
                <section key={release.version} aria-label={`Version ${release.version}`}>
                  <div className="flex items-baseline justify-between gap-3 mb-1">
                    <h3 className={`text-lg font-bold ${t.textPrimary}`}>
                      {release.title}
                      {release.version === APP_VERSION && (
                        <span className="ml-2 align-middle text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-600 text-white">Current</span>
                      )}
                    </h3>
                    <span className={`text-xs shrink-0 ${t.textMuted}`}>{release.date}</span>
                  </div>
                  <p className={`text-sm mb-3 ${t.textSecondary}`}>{release.summary}</p>
                  <ul className="space-y-2">
                    {release.changes.map((change, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className={`mt-0.5 shrink-0 text-[11px] font-bold px-2 py-0.5 rounded-full ${TYPE_STYLE[change.type]}`}>
                          {TYPE_LABEL[change.type]}
                        </span>
                        <span className={`text-sm ${t.textPrimary}`}>{change.text}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          </div>
        )}

        {/* Keyboard Shortcuts Modal */}
        {kind === 'shortcuts' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-2xl font-bold flex items-center gap-2 ${t.textPrimary}`}>
                <FileText className="w-6 h-6 text-blue-400" />
                Keyboard Shortcuts
              </h2>
              <button onClick={() => onClose()} className={`p-2 rounded-lg transition-all ${t.rowHover}`}>
                <X className={`w-5 h-5 ${t.textSecondary}`} />
              </button>
            </div>

            <div className={`space-y-6 ${t.textPrimary}`}>
              {SHORTCUT_GROUPS.map(group => (
                <div key={group.title}>
                  <h3 className={`text-lg font-bold mb-3 ${t.textPrimary}`}>{group.title}</h3>
                  <div className="space-y-2">
                    {group.items.map(([label, key]) => (
                      <div key={label} className={`flex justify-between items-center py-2 border-b ${t.divider}`}>
                        <span className="text-sm">{label}</span>
                        <kbd className={`px-3 py-1 rounded text-xs font-mono border ${dm ? 'bg-slate-800 border-slate-600' : 'bg-gray-100 border-gray-300'}`}>{key}</kbd>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <div className={`border rounded-lg p-4 ${dm ? 'bg-slate-800/50 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                <p className={`text-xs ${t.textSecondary}`}>
                  <strong>Note:</strong> Shortcuts do not work while you are typing in a box. The phone remote does the same jobs from a phone.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HelpModal;
